import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import { money } from '../money';
import { TrendingUp, Receipt, Users, ChevronRight, CheckCircle2, Upload, FilePlus2, CreditCard, Plus, DollarSign } from 'lucide-react';

const CARD = 'bg-white text-slate-800 border-slate-200';
const SECTION = 'rounded-lg border p-4 ' + CARD;

function Chips({ items, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {items.map(([key, Icon, label, onClick]) => (
        <button key={key} onClick={onClick} data-testid={`${testid}-${key}`}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm hover:bg-emerald-100">
          <Icon className="h-4 w-4" />{label}
        </button>
      ))}
    </div>
  );
}

function NeedsAttention({ n, testid, onCreate }) {
  return (
    <div className={SECTION} data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Needs attention</div>
      {n > 0 ? (
        <div className="text-sm text-slate-700">{n} item{n === 1 ? '' : 's'} need your attention.</div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-slate-600"><CheckCircle2 className="h-5 w-5 text-emerald-600" />You&apos;re all caught up
          <button className="ml-2 text-emerald-700 hover:underline" onClick={onCreate} data-testid={`${testid}-create-task`}>Create a task</button></div>
      )}
    </div>
  );
}

function Funnel({ title, steps, testid }) {
  return (
    <div className={SECTION} data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-400 mb-3">{title}</div>
      <div className="flex items-stretch gap-2">
        {steps.map((s, i) => (
          <React.Fragment key={s.key}>
            <div className="flex-1 rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid={`${testid}-${s.key}`}>
              <div className="text-xs text-slate-500">{s.label}</div>
              <div className="text-lg font-semibold tabular-nums">{money(s.amount)}</div>
              {s.sub != null && <div className="text-xs text-slate-400">{s.sub}</div>}
            </div>
            {i < steps.length - 1 && <div className="flex items-center text-slate-300"><ChevronRight className="h-5 w-5" /></div>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

const CHART_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const toDollars = (data, keys) => data.map((d) => { const o = { month: d.month }; keys.forEach((k) => { o[k] = (d[k] || 0) / 100; }); return o; });
const yr = new Date().getFullYear();

export function ExpensesOverview({ base, go }) {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${base}/accounting/expenses-overview`).then((r) => setD(r.data)).catch(() => setD(null)); }, [base]);
  const f = d?.funnel;
  const spend = d ? toDollars(d.spend, ['this_year', 'last_year']) : [];
  return (
    <div className="space-y-4 text-slate-800" data-testid="expenses-overview">
      <h2 className="text-xl font-semibold flex items-center gap-2"><Receipt className="h-5 w-5 text-emerald-600" />Expenses &amp; Pay Bills overview</h2>
      <Chips testid="expenses-chips" items={[
        ['upload-bills', Upload, 'Upload multiple bills', () => go('receipts')],
        ['create-bill', FilePlus2, 'Create bill', () => go('form', 'bill')],
        ['pay-bills', CreditCard, 'Pay bills', () => go('form', 'paybills')],
        ['record-expense', DollarSign, 'Record expense', () => go('form', 'expense')],
        ['show-all', Plus, 'Show all', () => go('expense_txns')],
      ]} />
      <div className="text-sm font-medium text-slate-600">Expenses at a glance</div>
      <div className="grid gap-4">
        <NeedsAttention n={d?.needs_attention || 0} testid="expenses-needs-attention" onCreate={() => go('form', 'bill')} />
        <Funnel title="Bills" testid="expenses-funnel" steps={[
          { key: 'for-review', label: 'For review', amount: f?.for_review?.amount || 0, sub: `${f?.for_review?.count || 0} items` },
          { key: 'unpaid', label: 'Unpaid', amount: f?.unpaid?.amount || 0, sub: `${f?.unpaid?.count || 0} bills` },
          { key: 'paid', label: 'Paid', amount: f?.paid?.amount || 0, sub: '--' },
        ]} />
        <div className={SECTION} data-testid="expenses-spend-over-time">
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-3">Spend over time</div>
          <div style={{ width: '100%', height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={spend} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} width={48} />
                <Tooltip formatter={(v) => money(Math.round(v * 100))} />
                <Legend />
                <Bar dataKey="last_year" name={`${yr - 1}`} fill="#cbd5e1" radius={[3, 3, 0, 0]} />
                <Bar dataKey="this_year" name={`${yr}`} fill="#059669" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <button className="text-emerald-700 text-sm hover:underline" onClick={() => go('vendors_page')} data-testid="expenses-go-vendors">Go to Vendors</button>
    </div>
  );
}

export function SalesOverview({ base, go, isNonprofit }) {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${base}/accounting/sales-overview`).then((r) => setD(r.data)).catch(() => setD(null)); }, [base]);
  const f = d?.funnel;
  const income = d ? toDollars(d.income, ['this_year', 'last_year']) : [];
  const invNoun = isNonprofit ? 'pledge' : 'invoice';
  return (
    <div className="space-y-4 text-slate-800" data-testid="sales-overview">
      <h2 className="text-xl font-semibold flex items-center gap-2"><TrendingUp className="h-5 w-5 text-emerald-600" />Sales &amp; Get Paid overview</h2>
      <Chips testid="sales-chips" items={[
        ['create-invoice', FilePlus2, `Create ${invNoun}`, () => go('form', 'invoice')],
        ['create-sales-receipt', Receipt, 'Create sales receipt', () => go('form', 'salesreceipt')],
        ['record-payment', DollarSign, 'Record payment', () => go('form', 'payment')],
        ['show-all', Plus, 'Show all', () => go('sales_txns')],
      ]} />
      <div className="text-sm font-medium text-slate-600">Sales at a glance</div>
      <div className="grid gap-4">
        <NeedsAttention n={d?.needs_attention || 0} testid="sales-needs-attention" onCreate={() => go('form', 'invoice')} />
        <Funnel title="Last 30 days" testid="sales-funnel" steps={[
          { key: 'not-paid', label: 'Not Paid', amount: f?.not_paid?.amount || 0, sub: `${f?.not_paid?.count || 0} open` },
          { key: 'paid', label: 'Paid', amount: f?.paid?.amount || 0, sub: 'last 30 days' },
          { key: 'deposited', label: 'Deposited', amount: f?.deposited?.amount || 0, sub: 'last 30 days' },
        ]} />
        <div className={SECTION} data-testid="sales-income-over-time">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wide text-slate-400">Income over time</div>
            <div className="text-xs text-slate-400">This year · compared to {yr - 1}</div>
          </div>
          <div style={{ width: '100%', height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={income} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} width={48} />
                <Tooltip formatter={(v) => money(Math.round(v * 100))} />
                <Legend />
                <Line type="monotone" dataKey="last_year" name={`${yr - 1}`} stroke="#cbd5e1" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="this_year" name={`${yr}`} stroke="#059669" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <button className="text-emerald-700 text-sm hover:underline" onClick={() => go('customers_leads')} data-testid="sales-go-customers">Go to Customers &amp; leads</button>
    </div>
  );
}

export function CustomerHubOverview({ base, go }) {
  const [cust, setCust] = useState([]);
  const [funnel, setFunnel] = useState(null);
  useEffect(() => {
    axios.get(`${base}/reports/customer-balances`).then((r) => setCust(r.data?.rows || r.data || [])).catch(() => setCust([]));
    axios.get(`${base}/accounting/customer-hub`).then((r) => setFunnel(r.data)).catch(() => setFunnel(null));
  }, [base]);
  const withBal = cust.filter((c) => (c.balance || 0) !== 0);
  const steps = [
    ['open_opportunities', 'Open opportunities'], ['open_estimates', 'Open estimates'],
    ['open_contracts', 'Open contracts'], ['in_progress_projects', 'In progress projects'],
    ['unpaid_invoices', 'Unpaid invoices'], ['reviews', 'Reviews'],
  ];
  return (
    <div className="space-y-4 text-slate-800" data-testid="customerhub-overview">
      <div className="text-sm text-slate-500" data-testid="customerhub-breadcrumb">Customer Hub / Overview</div>
      <h2 className="text-xl font-semibold flex items-center gap-2"><Users className="h-5 w-5 text-emerald-600" />Customers at a glance</h2>
      <div className={SECTION} data-testid="customerhub-funnel">
        <div className="text-xs uppercase tracking-wide text-slate-400 mb-3">Last 365 days</div>
        <div className="flex items-stretch gap-1 overflow-x-auto">
          {steps.map(([key, label], i) => {
            const node = funnel?.[key] || {};
            const count = node.count;
            return (
              <React.Fragment key={key}>
                <div className="flex-1 min-w-[120px] rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid={`customerhub-step-${key}`}>
                  <div className="text-xs text-slate-500">{label}</div>
                  <div className="text-lg font-semibold tabular-nums">{count == null ? '--' : count}</div>
                  {key === 'unpaid_invoices' && (node.overdue || 0) > 0 && <span className="inline-block mt-1 text-[11px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700" data-testid="customerhub-overdue">{node.overdue} overdue</span>}
                </div>
                {i < steps.length - 1 && <div className="flex items-center text-slate-300"><ChevronRight className="h-4 w-4" /></div>}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className={`rounded-lg border p-3 ${CARD}`} data-testid="hub-card-customers"><div className="text-xs text-slate-500">Customers</div><div className="text-lg font-semibold">{cust.length}</div></div>
        <div className={`rounded-lg border p-3 ${CARD}`} data-testid="hub-card-withbal"><div className="text-xs text-slate-500">With open balance</div><div className="text-lg font-semibold tabular-nums">{money(withBal.reduce((s, c) => s + (c.balance || 0), 0))}</div><div className="text-xs text-slate-400">{withBal.length} customers</div></div>
      </div>
      <div className={`rounded-lg border overflow-hidden ${CARD}`}>
        <table className="w-full text-sm" data-testid="customerhub-balances">
          <thead><tr className="text-left text-slate-500 border-b border-slate-200"><th className="p-2.5">Customer</th><th className="p-2.5 text-right">Open balance</th></tr></thead>
          <tbody>
            {withBal.map((r, i) => <tr key={i} className="border-b border-slate-100"><td className="p-2.5">{r.name}</td><td className="p-2.5 text-right tabular-nums">{money(r.balance || 0)}</td></tr>)}
            {withBal.length === 0 && <tr><td colSpan={2} className="p-4 text-slate-500">No customer balances.</td></tr>}
          </tbody>
        </table>
      </div>
      <button className="text-emerald-700 text-sm hover:underline" onClick={() => go('customers_leads')} data-testid="hub-go-customers">Go to Customers &amp; leads</button>
    </div>
  );
}
